import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import EmptyState from "@/components/base/EmptyState";
import { cn } from "@/utils/cn";
import { formatDate, formatFcfa } from "@/utils/format";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import { PLATFORM_SCOPE_LABELS, platformLocataireNomComplet } from "@/store/platform/types";
import {
  BIEN_STATUT_LABELS,
  BIEN_STATUT_TONES,
  BIEN_TYPE_ICONS,
  BIEN_TYPE_LABELS,
  type BienStatut,
  type BienType,
} from "@/pages/proprietaire/biens/types";
import PlatformBienForm from "@/pages/super-admin/components/PlatformBienForm";

export default function SuperAdminBienDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getBien, getImmeuble, getLocataire, removeBien, countContratsByBien, contratActifByBien } = usePlatformData();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const bien = id ? getBien(id) : undefined;

  if (!bien) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/super-admin/biens"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux biens
        </Link>
        <EmptyState
          icon="ri-home-4-line"
          title="Bien introuvable"
          description="Ce bien n'existe pas ou n'est plus disponible."
          action={
            <Link
              to="/super-admin/biens"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les biens
            </Link>
          }
        />
      </div>
    );
  }

  const immeuble = getImmeuble(bien.immeubleId);
  const type = bien.type as BienType;
  const statut = bien.statut as BienStatut;
  const contratActif = contratActifByBien(bien.id);
  const locataireActuel = contratActif ? getLocataire(contratActif.locataireId) : undefined;
  const nbContrats = countContratsByBien(bien.id);

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Type", value: BIEN_TYPE_LABELS[type], icon: BIEN_TYPE_ICONS[type] },
    { label: "Espace", value: PLATFORM_SCOPE_LABELS[bien.scope], icon: "ri-layout-grid-line" },
    { label: "Immeuble", value: immeuble?.nom ?? "Immeuble inconnu", icon: "ri-building-2-line" },
    { label: "Étage", value: bien.etage || "—", icon: "ri-stack-line" },
    { label: "Numéro", value: bien.numero || "—", icon: "ri-hashtag" },
    { label: "Surface", value: bien.surface > 0 ? `${bien.surface} m²` : "—", icon: "ri-ruler-2-line" },
    { label: "Loyer mensuel", value: formatFcfa(bien.loyerMensuel), icon: "ri-money-cny-circle-line" },
    { label: "Contrats", value: String(nbContrats), icon: "ri-file-text-line" },
    {
      label: "Locataire actuel",
      value: locataireActuel ? platformLocataireNomComplet(locataireActuel) : "Aucun contrat actif",
      icon: "ri-user-3-line",
    },
    { label: "Créé le", value: formatDate(bien.createdAt), icon: "ri-calendar-line" },
  ];

  const confirmDelete = () => {
    removeBien(bien.id);
    setDeleteOpen(false);
    navigate("/super-admin/biens");
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/super-admin/biens"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux biens
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <i className={cn(BIEN_TYPE_ICONS[type], "text-2xl")} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={BIEN_STATUT_TONES[statut]}>{BIEN_STATUT_LABELS[statut]}</Badge>
              <Badge tone={bien.scope === "agence" ? "accent" : "primary"}>{PLATFORM_SCOPE_LABELS[bien.scope]}</Badge>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {bien.reference || BIEN_TYPE_LABELS[type]}
            </h2>
            <p className="mt-1 text-sm text-foreground-600">
              {BIEN_TYPE_LABELS[type]} · {immeuble?.nom ?? "Immeuble inconnu"}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-edit-line text-base" aria-hidden="true" />
            Modifier
          </button>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            Supprimer
          </button>
        </div>
      </section>

      <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
            <i className="ri-information-line text-base" aria-hidden="true" />
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Informations du bien</h3>
        </header>
        <div className="p-4 md:p-5">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {infos.map((info) => (
              <div key={info.label} className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                  <i className={cn(info.icon, "text-base")} aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <dt className="text-xs text-foreground-500">{info.label}</dt>
                  <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                </div>
              </div>
            ))}
          </dl>
          {(bien.description || bien.infosComplementaires) && (
            <div className="mt-5 flex flex-col gap-4 border-t border-background-200 pt-4">
              {bien.description && (
                <div>
                  <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                    Description
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{bien.description}</p>
                </div>
              )}
              {bien.infosComplementaires && (
                <div>
                  <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                    Informations complémentaires
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{bien.infosComplementaires}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <PlatformBienForm open={editOpen} onClose={() => setEditOpen(false)} bien={bien} />

      <ConfirmDeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        description={`Supprimer le bien « ${bien.reference || BIEN_TYPE_LABELS[type]} » ? Cette action est définitive.`}
        blockedMessage={
          nbContrats > 0
            ? `Ce bien est relié à ${nbContrats} contrat(s). Supprimez ou clôturez d'abord ses contrats.`
            : undefined
        }
      />
    </div>
  );
}